import { MessageCircle, Phone, Star, Sparkles, Zap } from "lucide-react";
import VideoPlayer from "@/components/landing/VideoPlayer";
import IPhoneFrame from "@/components/landing/IPhoneFrame";
import { trackWhatsappClick, trackPhoneClick } from "@/lib/tracking";

export default function Hero({ settings }) {
  const whatsappUrl = `https://wa.me/${settings.whatsapp_number}`;
  const phoneUrl = `tel:${settings.phone_number}`;

  return (
    <section
      className="relative overflow-hidden border-b border-white/10"
      data-testid="hero-section"
    >
      {/* Atmospheric background layers */}
      <div aria-hidden="true" className="absolute inset-0 pointer-events-none">
        {/* Top-left gold glow */}
        <div className="absolute -top-40 -left-32 w-[40rem] h-[40rem] bg-mfp-gold/15 blur-[120px] rounded-full" />
        {/* Bottom-right ambient gold */}
        <div className="absolute -bottom-40 -right-20 w-[36rem] h-[36rem] bg-mfp-goldDeep/15 blur-[120px] rounded-full" />
        {/* Subtle grid pattern */}
        <div
          className="absolute inset-0 opacity-[0.04]"
          style={{
            backgroundImage:
              "linear-gradient(rgba(245,230,200,0.4) 1px, transparent 1px), linear-gradient(90deg, rgba(245,230,200,0.4) 1px, transparent 1px)",
            backgroundSize: "56px 56px",
          }}
        />
        {/* Vignette */}
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-mfp-bg" />
      </div>

      <div className="relative max-w-6xl mx-auto px-4 md:px-8 pt-10 pb-16 md:pt-20 md:pb-28">
        <div className="grid lg:grid-cols-[1.05fr_0.95fr] gap-10 md:gap-12 lg:gap-16 items-center">
          {/* LEFT — Copy + CTAs */}
          <div className="mfp-reveal">
            {/* Social-proof pill */}
            <div
              className="inline-flex items-center gap-2.5 border border-white/15 bg-white/[0.03] backdrop-blur px-3.5 py-1.5 text-[11px] uppercase tracking-[0.22em] text-mfp-muted mb-6 rounded-full"
              data-testid="hero-badge"
            >
              <Sparkles className="h-3 w-3 text-mfp-gold" />
              <span>Reel-Produktion · Frankfurt &amp; Rhein-Main</span>
            </div>

            <h1
              className="font-display text-4xl sm:text-5xl md:text-6xl lg:text-[4.25rem] leading-[1.02] uppercase tracking-tighter"
              data-testid="hero-headline"
            >
              Wir verwandeln deine <br className="hidden sm:block" />
              <span className="text-mfp-gold">Reels</span> in echte<br />
              <span className="text-mfp-gold">Kundenanfragen</span>
            </h1>

            <p
              className="mt-6 md:mt-8 text-base md:text-xl text-mfp-muted max-w-xl font-medium leading-relaxed"
              data-testid="hero-subheadline"
            >
              {settings.hero_subheadline}
            </p>

            {/* Social proof row */}
            <div className="mt-7 md:mt-8 flex items-center gap-4 flex-wrap" data-testid="hero-social-proof">
              <div className="flex items-center gap-0.5">
                {[0, 1, 2, 3, 4].map((i) => (
                  <Star key={i} className="h-4 w-4 fill-mfp-gold text-mfp-gold" />
                ))}
              </div>
              <span className="text-sm text-white/80 font-medium">
                Vertrauen von Unternehmen aus Frankfurt, Aschaffenburg &amp; Darmstadt
              </span>
            </div>

            {/* CTA buttons */}
            <div className="mt-8 md:mt-10 flex flex-col sm:flex-row gap-3">
              <a
                href={whatsappUrl}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => trackWhatsappClick("hero")}
                className="mfp-btn mfp-wa-btn group relative text-white text-sm md:text-base px-7 py-5 flex items-center justify-center gap-3 rounded-xl mfp-pulse overflow-hidden"
                data-testid="hero-whatsapp-cta"
              >
                <span className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/15 to-white/0 -translate-x-full group-hover:translate-x-full transition-transform duration-700" />
                <MessageCircle className="h-5 w-5 relative" />
                <span className="relative">Jetzt WhatsApp schreiben</span>
              </a>
              <a
                href={phoneUrl}
                onClick={() => trackPhoneClick("hero")}
                className="mfp-btn bg-white/[0.03] backdrop-blur border-2 border-mfp-gold/40 text-mfp-champagne text-sm md:text-base px-7 py-5 flex items-center justify-center gap-3 hover:bg-mfp-gold hover:text-black hover:border-mfp-gold transition-all rounded-xl"
                data-testid="hero-call-cta"
              >
                <Phone className="h-5 w-5" />
                Jetzt anrufen
              </a>
            </div>

            {/* Trust indicators */}
            <div className="mt-10 grid grid-cols-3 gap-4 md:gap-6 max-w-md" data-testid="hero-trust-row">
              <TrustItem icon={Zap} label="Antwort < 2h" />
              <TrustItem icon={Sparkles} label="Lieferung 7–14 Tage" />
              <TrustItem icon={Star} label="2 Korrekturen inkl." />
            </div>
          </div>

          {/* RIGHT — iPhone with video */}
          <div
            className="relative flex items-center justify-center lg:mfp-float lg:py-4"
            data-testid="hero-iphone"
            style={{ animationDelay: "200ms" }}
          >
            <IPhoneFrame>
              <VideoPlayer
                url={settings.hero_video_url}
                title="MediaFly Reel Showcase"
                vertical
              />
            </IPhoneFrame>
          </div>
        </div>
      </div>
    </section>
  );
}

function TrustItem({ icon: Icon, label }) {
  return (
    <div className="flex flex-col items-start gap-1.5">
      <Icon className="h-4 w-4 text-mfp-gold" />
      <span className="text-xs uppercase tracking-widest text-white/70 font-bold leading-tight">
        {label}
      </span>
    </div>
  );
}
